import { AuthProvider } from 'react-admin';
import { defaultApiClient } from './api-client';
import { persistStorageService } from '@/features/auth/persist-storage';
import { AuthResponse, UserProfile } from '@/features/auth/auth.type';

const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    const response = await defaultApiClient.post(`/api/v1/auth/sign_in`, {
      email: username,
      password,
    });

    if (response.status < 200 || response.status >= 300) {
      const error = response.data;
      throw new Error(error.detail || 'Login failed');
    }

    // TODO: fix type using axios response type
    const auth: AuthResponse = response.data;

    persistStorageService.setPersistItem({
      name: 'token',
      value: auth.token,
    });
    persistStorageService.setPersistItem({
      name: 'refresh_token',
      value: auth.refresh_token,
    });

    if (auth.user) {
      persistStorageService.setPersistItem({
        name: 'user',
        value: auth.user,
        stringify: true,
      });
    }
    persistStorageService.setPersistItem({
      name: 'expires_at',
      value: auth.expires_at.toString(),
    });

    return Promise.resolve();
  },

  logout: async () => {
    try {
      await defaultApiClient.post(`/api/v1/auth/sign_out`);
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with logout even if API request fails
    }

    // Always clear localStorage
    persistStorageService.batchRemovePersistItems({
      names: ['token', 'refresh_token', 'user', 'expires_at'],
    });

    return Promise.resolve();
  },

  checkError: ({ status }: { status?: number }) => {
    if (status === 401 || status === 403) {
      persistStorageService.batchRemovePersistItems({
        names: ['token', 'refresh_token', 'user', 'expires_at'],
      });
      return Promise.reject();
    }
    return Promise.resolve();
  },

  checkAuth: async () => {
    const token = persistStorageService.getPersistItem<string>({
      name: 'token',
    });

    if (!token) {
      return Promise.reject(new Error('No token found'));
    }

    // Check if token is expired
    const expiresAt = persistStorageService.getPersistItem<string>({
      name: 'expires_at',
    });
    const isExpired = expiresAt && parseInt(expiresAt, 10) * 1000 < Date.now();

    // If token is not expired, return success immediately
    if (!isExpired) {
      return Promise.resolve();
    }

    // If token is expired, attempt to refresh it
    try {
      console.log('Token expired, attempting to refresh...');
      const response = await defaultApiClient.post(
        `/api/v1/auth/validate_token`,
        {
          access_token: token,
          refresh_token:
            persistStorageService.getPersistItem<string>({
              name: 'refresh_token',
            }) || '',
        },
      );

      if (response.status !== 200) {
        console.log('Token refresh failed');
        // If refresh fails, clear storage and reject
        persistStorageService.batchRemovePersistItems({
          names: ['token', 'refresh_token', 'user', 'expires_at'],
        });
        return Promise.reject(new Error('Token refresh failed'));
      }

      // Parse the refreshed token response
      const refreshedAuth: AuthResponse = response.data;
      console.log('Token refreshed successfully');

      // Store the new tokens
      if (refreshedAuth.token) {
        persistStorageService.setPersistItem({
          name: 'token',
          value: refreshedAuth.token,
          options: { path: '/', expires: refreshedAuth.expires_at },
        });
      }

      if (refreshedAuth.refresh_token) {
        persistStorageService.setPersistItem({
          name: 'refresh_token',
          value: refreshedAuth.refresh_token,
        });
      }

      if (refreshedAuth.user) {
        persistStorageService.setPersistItem({
          name: 'user',
          value: JSON.stringify(refreshedAuth.user),
        });
      }

      if (refreshedAuth.expires_at) {
        persistStorageService.setPersistItem({
          name: 'expires_at',
          value: refreshedAuth.expires_at.toString(),
        });
      }

      return Promise.resolve();
    } catch (error) {
      console.error('Error refreshing token:', error);

      // In case of network errors, we could either:
      // 1. Reject and force logout (more secure)
      // 2. Resolve and allow access (better UX but less secure)
      // Choosing option 1 for better security:
      persistStorageService.batchRemovePersistItems({
        names: ['token', 'refresh_token', 'user', 'expires_at'],
      });
      return Promise.reject(new Error('Token refresh failed'));
    }
  },

  getIdentity: async () => {
    const userJson = persistStorageService.getPersistItem<UserProfile>({
      name: 'user',
      parse: true,
    });

    if (!userJson) {
      const response = await defaultApiClient.get(`/api/v1/auth/me`);
      console.log('response - getIdentity', response);

      if (response.status === 401) {
        persistStorageService.batchRemovePersistItems({
          names: ['token', 'refresh_token', 'user', 'expires_at'],
        });
        return Promise.reject(new Error('No user found'));
      }

      if (response.status !== 200) {
        return Promise.reject(new Error('No user found'));
      }
      const user: UserProfile = response.data;
      persistStorageService.setPersistItem({
        name: 'user',
        value: JSON.stringify(user),
      });
    }

    try {
      const user: UserProfile = userJson as UserProfile;
      return Promise.resolve({
        id: user.id,
        fullName: user.fullName,
        avatar: user.avatar,
        email: user.email,
      });
    } catch (error) {
      console.error('Error getting identity:', error);
      return Promise.reject(new Error('Invalid user data'));
    }
  },

  getPermissions: async () => {
    if (window.location.pathname === '/complete-signup') {
      return Promise.resolve();
    }

    let userJson = persistStorageService.getPersistItem<UserProfile>({
      name: 'user',
      parse: true,
    });
    if (!userJson) {
      const response = await defaultApiClient.get(`/api/v1/auth/me`);
      console.log('response - getPermissions', response);
      if (response.status !== 200) {
        return Promise.reject(new Error('No user found'));
      }
      const user: UserProfile = response.data;
      persistStorageService.setPersistItem({
        name: 'user',
        value: JSON.stringify(user),
      });
      userJson = user;
    }

    try {
      const user = userJson;
      return Promise.resolve(user.role);
    } catch (error) {
      console.error('Error getting permissions:', error);
      return Promise.reject(new Error('Invalid user data'));
    }
  },
};

export default authProvider;
